import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ProjectSize } from '@/types/api'
import { projectSizeOptions } from '@/lib/leadOptions'

export interface TransitionDialogResult {
  projectSize?: ProjectSize
  disqualificationReason?: string
}

interface TransitionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 'won' shows the project-size picker, 'lost' shows the reason textarea. */
  mode: 'won' | 'lost'
  /** Optional qualifier for the title, e.g. 'Project' → "Mark Project as Won". */
  entityLabel?: string
  isPending?: boolean
  onConfirm: (result: TransitionDialogResult) => void
}

/**
 * Reusable confirmation dialog for Won/Lost status transitions.
 *
 * For 'won' it collects the required project size; for 'lost' it collects a
 * disqualification reason. Owns its own input state and resets on close, so
 * callers only wire `open`/`onOpenChange` and handle `onConfirm`.
 */
export function TransitionDialog({
  open,
  onOpenChange,
  mode,
  entityLabel,
  isPending,
  onConfirm,
}: TransitionDialogProps) {
  const [projectSize, setProjectSize] = useState<ProjectSize | ''>('')
  const [reason, setReason] = useState('')

  // Reset inputs whenever the dialog is closed.
  useEffect(() => {
    if (!open) {
      setProjectSize('')
      setReason('')
    }
  }, [open])

  const label = entityLabel ? `${entityLabel} ` : ''
  const title = mode === 'won' ? `Mark ${label}as Won` : `Mark ${label}as Lost`
  const description =
    mode === 'won'
      ? 'Please confirm the project size for this won deal.'
      : 'Please provide a reason for disqualifying this record.'

  const canConfirm = mode === 'won' ? !!projectSize : !!reason

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm(
      mode === 'won'
        ? { projectSize: projectSize as ProjectSize }
        : { disqualificationReason: reason }
    )
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {mode === 'lost' ? (
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being marked as lost?"
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Project Size</Label>
            <Select
              value={projectSize}
              onValueChange={(v) => setProjectSize(v as ProjectSize)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project size" />
              </SelectTrigger>
              <SelectContent>
                {projectSizeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || isPending}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
